import type { Directive, DirectiveBinding } from "vue";

/**
 * Vue directive to fix web component (mc-input, etc.) width calculation issue [ISCE-13324]s during SPA navigation
 *
 * Web components calculate their dimensions when they first connect to the DOM.
 * During SPA navigation, parent containers may not have finished layout calculations,
 * causing web components to calculate incorrect widths.
 *
 * This directive forces a re-render of web components after the layout has stabilized.
 *
 * Usage:
 * <mc-input v-web-component-fix ... />
 */

// Type-safe WeakMap to store ResizeObserver instances per element (avoids `el as any` casts)
const observerMap = new WeakMap<HTMLElement, ResizeObserver>();

// Debounce delay in ms applied to the ResizeObserver callback to avoid excessive updates
const RESIZE_DEBOUNCE_DELAY = 100;

const forceWebComponentUpdate = (el: HTMLElement) => {
  // Use multiple requestAnimationFrame to ensure layout is completely stable
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Force the web component to update by toggling a property
      // This triggers the web component's lifecycle hooks
      const hasHidden = el.hasAttribute("hidden");

      // Temporarily hide and show to trigger re-render
      el.setAttribute("hidden", "");

      // Use another RAF to ensure the hidden state is processed
      requestAnimationFrame(() => {
        if (!hasHidden) {
          el.removeAttribute("hidden");
        }

        // Trigger any internal resize handlers the component might have
        if (el.shadowRoot) {
          // Dispatch a custom event that web components might listen to
          el.dispatchEvent(new Event("resize", { bubbles: false }));
        }
      });
    });
  });
};

/**
 * Web Component Fix Directive
 * Automatically fixes layout issues with web components after mount
 */
export const webComponentFix: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    // Only apply if enabled (default: true)
    if (binding.value === false) {
      return;
    }

    // Apply fix after component mounts
    forceWebComponentUpdate(el);

    // Store cleanup function if needed
    if (binding.modifiers.persistent) {
      // Guard: do not create an observer when there is no parent to observe
      if (!el.parentElement) {
        return;
      }

      // Debounce the callback so that rapid/continuous resize events (e.g. window
      // resize animations) do not trigger forceWebComponentUpdate on every frame.
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      const resizeObserver = new ResizeObserver(() => {
        if (debounceTimer !== null) {
          clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          forceWebComponentUpdate(el);
        }, RESIZE_DEBOUNCE_DELAY);
      });

      resizeObserver.observe(el.parentElement);

      // Store observer in the WeakMap for type-safe cleanup in unmounted
      observerMap.set(el, resizeObserver);
    }
  },

  unmounted(el: HTMLElement) {
    // Cleanup ResizeObserver via the WeakMap (no unsafe `el as any` casts)
    const observer = observerMap.get(el);
    if (observer) {
      observer.disconnect();
      observerMap.delete(el);
    }
  }
};

export default webComponentFix;
